import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, map, Observable } from "rxjs";

import { environment } from "@env/environment";
import { UrlBuilder } from "@app/shared/helpers";
import { ChangePasswordResponse, LoginUserResponse, UserResetPassword, UserSetPassword, UserToken } from "@app/shared/models/user";

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private readonly http: HttpClient = inject(HttpClient);

  private readonly userTokenSubject = new BehaviorSubject<UserToken | null>(null);
  public readonly userToken$: Observable<UserToken | null> = this.userTokenSubject.asObservable();

  public login(user: string, password: string): Observable<UserToken> {
    const url: string = UrlBuilder
      .create(environment.apiUrl)
      .addQuerySegments('portal', 'login')
      .build();

    return this.http.post<LoginUserResponse>(url, {
      Login: user,
      Password: password,
      AppDomainName: environment.identifier
    })
      .pipe(
        map((response: LoginUserResponse) => ({
          value: response.token,
          expires: response.expirationDate
        } as UserToken))
      );
  }

  /**
   * Sends a reset password email to the given user.
   * @param userResetPassword User reset password information
   * @returns An observable which returns true if the email has been sent; false otherwise.
   */
  public sendResetPasswordEmail(userResetPassword: UserResetPassword): Observable<boolean> {
    const url: string = UrlBuilder
      .create(environment.apiUrl)
      .addQuerySegments('accounts', 'password-reset', 'request')
      .build();

    return this.http.post<boolean>(url, userResetPassword);
  }

  /**
   * Validates the reset password token.
   * @param token Reset password token.
   * @returns An observable which returns true if the given token is valid; false otherwise.
   */
  public getResetPasswordTokenValidity(token: string): Observable<boolean> {
    const url: string = UrlBuilder
      .create(environment.apiUrl)
      .addQuerySegments('accounts', 'password-reset')
      .addQueryParam('token', token)
      .build();
    return this.http.get<boolean>(url);
  }

  /**
   * Updates the user password.
   * @param token Password token
   * @param userPasswords The passwords that the user have typed.
   * @returns An observable which returns true if the password has been updated; false otherwise.
   */
  public updatePassword(token: string, userPasswords: UserSetPassword): Observable<boolean> {
    const url: string = UrlBuilder
      .create(environment.apiUrl)
      .addQuerySegments('accounts', 'password-reset')
      .addQueryParam('token', token)
      .build();
    return this.http.post<boolean>(url, userPasswords);
  }

  public changePassword(email: string, formerPassword: string, newPassword: string): Observable<ChangePasswordResponse> {
    const url: string = UrlBuilder.create(environment.apiUrl)
      .addQuerySegments('portal', 'pwd', 'change')
      .build();
    return this.http.post<ChangePasswordResponse>(url, { Login: email, OldPassword: formerPassword, NewPassword: newPassword });
  }

  public activateAccount(token: string, passwords: UserSetPassword): Observable<boolean> {
    const url: string = UrlBuilder
      .create(environment.apiUrl)
      .addQuerySegments('accounts', 'activation', token)
      .build();
    return this.http.post<boolean>(url, passwords);
  }

  /**
   * Checks if the user account is activated using the given activation token.
   * @param token User activation token.
   * @returns An observable which returns true if the user account is activated; false otherwise.
   */
  public checkActivatedAccount(token: string): Observable<boolean> {
    const url: string = UrlBuilder
      .create(environment.apiUrl)
      .addQuerySegments('accounts', 'activation', token)
      .build();
    return this.http.get<boolean>(url);
  }
}
